import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Toaster } from 'react-hot-toast'
import { LanguageProvider } from '@/i18n/LanguageProvider'

/**
 * Last-resort boundary. A crash inside a lazily loaded zone (cabinet, modeler)
 * must not take the marketing site down with it — the user keeps a way out.
 */
class RootErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Unhandled UI error', error, info.componentStack)
  }

  render() {
    if (!this.state.error) return this.props.children
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 'var(--space-8)' }}>
        <div style={{ maxWidth: '32rem', textAlign: 'center', display: 'grid', gap: 'var(--space-4)' }}>
          <h1 style={{ fontSize: 'var(--text-2xl)', fontWeight: 700 }}>Something broke</h1>
          <p style={{ color: 'var(--color-text-muted)' }}>{this.state.error.message}</p>
          <div>
            <a className="btn btn-primary btn-md" href="/">
              Back to the homepage
            </a>
          </div>
        </div>
      </div>
    )
  }
}

export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <RootErrorBoundary>
      <LanguageProvider>
        <Toaster
          position="top-right"
          toastOptions={{ style: { fontFamily: 'Geist, sans-serif', fontSize: '14px' } }}
        />
        {children}
      </LanguageProvider>
    </RootErrorBoundary>
  )
}
